<div class="grouped-datatable">
  @if (isInitialDataLoading() && loaderConfig()) {
    <app-grouped-skeleton-loader [config]="loaderConfig()!" />
  } @else {
    @if (configuration().isSearchEnabled) {
      <div class="flex items-center justify-between gap-4 relative">
        <div class="relative w-full max-w-[320px]">
          <app-icon class="absolute left-3 top-1/2 -translate-y-1/2" src="/icons/search.svg" alt="Search" [size]="16" />
          <input
            class="h-10 w-full rounded-xl border border-shikenso-purple-base-12 bg-shikenso-base-white pl-10 pr-3 text-sm"
            type="text"
            [formControl]="searchControl"
            [placeholder]="'shared.search' | translate"
          />
        </div>
      </div>
    }

    @if (configuration().showExpandAllToggle && filteredGroups().length > 0) {
      <div class="absolute right-4 top-2">
        <button
          mat-flat-button
          type="button"
          color="secondary-gray"
          class="mat-micro-button"
          (click)="toggleAllGroups()"
        >
          <span class="text-xs">
            {{
              allExpanded()
                ? ('components.groupedDatatable.collapseAll' | translate)
                : ('components.groupedDatatable.expandAll' | translate)
            }}
          </span>
        </button>
      </div>
    }

    <div class="table-container simple-hover-table grouped-datatable-table">
      <table
        mat-table
        matSort
        multiTemplateDataRows
        [dataSource]="pagedGroups()"
        [matSortActive]="sortState().active"
        [matSortDirection]="sortState().direction"
        (matSortChange)="onSortChanged($event)"
      >
        @for (column of parentColumnsWithTemplate(); track column.id) {
          <ng-container [matColumnDef]="column.id">
            <th
              mat-header-cell
              class="grouped-parent-header-cell"
              *matHeaderCellDef
              mat-sort-header
              [disabled]="!column.isSortable"
              [ngClass]="getColumnClasses(column)"
            >
              {{ column.label | translate }}
            </th>
            <td mat-cell class="grouped-parent-cell" *matCellDef="let group" [ngClass]="column.cellClasses ?? []">
              @if (column.template) {
                <ng-container *ngTemplateOutlet="column.template; context: { row: group.parent }" />
              } @else {
                {{ getCellValue(column, group.parent) }}
              }
            </td>
          </ng-container>
        }

        <ng-container matColumnDef="expand">
          <th mat-header-cell class="grouped-parent-header-cell grouped-expand-column" *matHeaderCellDef></th>
          <td mat-cell class="grouped-parent-cell grouped-expand-column" *matCellDef="let group">
            <button mat-icon-button type="button" (click)="onGroupExpandToggle(group.id, $event)">
              <app-icon
                [src]="isGroupExpanded(group.id) ? '/icons/chevron-up.svg' : '/icons/chevron-down.svg'"
                [size]="16"
              />
            </button>
          </td>
        </ng-container>

        <ng-container matColumnDef="expandedDetail">
          <td
            mat-cell
            class="grouped-detail-cell"
            *matCellDef="let group"
            [attr.colspan]="parentDisplayedColumnIds().length"
          >
            @if (isGroupExpanded(group.id)) {
              <div class="grouped-child-wrapper" [style.padding-left]="getChildIndentStyle()">
                <table class="grouped-child-table">
                  @if (configuration().isChildHeaderEnabled) {
                    <thead>
                      <tr>
                        @for (column of childColumnsWithTemplate(); track column.id) {
                          <th class="grouped-child-header-cell" [ngClass]="getColumnClasses(column)">
                            {{ column.label | translate }}
                          </th>
                        }
                      </tr>
                    </thead>
                  }

                  <tbody>
                    @for (child of group.children; track child.id; let index = $index) {
                      <tr
                        class="grouped-child-row"
                        data-testid="grouped-datatable-child-row"
                        [class.bg-shikenso-gray-base]="index % 2 === 0"
                        [class.bg-shikenso-base-white]="index % 2 !== 0"
                        [class.grouped-child-row--clickable]="configuration().isChildRowClickEnabled"
                        [class.!cursor-default]="!configuration().isChildRowClickEnabled"
                        (click)="onChildRowClicked($event, group.parent, child)"
                      >
                        @for (column of childColumnsWithTemplate(); track column.id) {
                          <td class="grouped-child-cell" [ngClass]="column.cellClasses ?? []">
                            @if (column.template) {
                              <ng-container
                                *ngTemplateOutlet="column.template; context: { row: child, parent: group.parent }"
                              />
                            } @else {
                              {{ getCellValue(column, child) }}
                            }
                          </td>
                        }
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            }

            @if (configuration().rowSeparatorEnabled && !isLastPagedGroup(group)) {
              <div class="grouped-parent-separator" role="presentation"></div>
            }
          </td>
        </ng-container>

        <tr
          mat-header-row
          *matHeaderRowDef="parentDisplayedColumnIds()"
          class="header-row grouped-parent-header-row"
        ></tr>
        <tr
          mat-row
          *matRowDef="let group; columns: parentDisplayedColumnIds()"
          class="grouped-parent-row"
          data-testid="grouped-datatable-parent-row"
          [class.grouped-parent-row--toggleable]="configuration().isParentRowToggleEnabled"
          [class.!cursor-default]="!configuration().isParentRowToggleEnabled"
          (click)="onParentRowToggled($event, group.id, group.parent)"
        ></tr>
        <tr mat-row *matRowDef="let group; columns: ['expandedDetail']" class="grouped-detail-row"></tr>
      </table>

      @if (pagedGroups().length === 0 && !loadingData()) {
        <div class="py-6 text-center text-sm text-shikenso-purple-base-60">
          {{ 'shared.noResults' | translate }}
        </div>
      }
    </div>

    @if (configuration().isPaginationEnabled) {
      <mat-paginator
        class="custom-paginator"
        [class.!hidden]="isInitialDataLoading()"
        [length]="paginatorLength()"
        [pageIndex]="paginatorState().pageIndex"
        [pageSize]="paginatorState().pageSize"
        [pageSizeOptions]="configuration().pageSizeOptions"
        (page)="onPageChanged($event)"
      />
    }
  }
</div>
